import { emitter, uuid } from '@/package';

class _AppWorker<
  W extends {
    [workerName: string]: {
      [x: string]: (...args: any[]) => Promise<any>;
    };
  } = {},
> {
  static instance: Nullish<_AppWorker>;

  private _workerScripts = {} as {
    [K in keyof W]: Nullish<Worker>;
  };

  constructor() {
    if (_AppWorker.instance) return _AppWorker.instance as _AppWorker<W>;

    _AppWorker.instance = this;
  }

  add<
    N extends string,
    WT extends {
      [x: string]: any;
    },
  >(name: N, WorkerConstructor: new (...args: any[]) => Worker) {
    if (import.meta.env.MODE === 'test') return this;

    if ((this._workerScripts as any)[name as any])
      return this as unknown as _AppWorker<
        W & {
          [K in N]: WT;
        }
      >;

    const worker = new WorkerConstructor();

    this._workerScripts = {
      ...this._workerScripts,
      [`${name}`]: worker,
    };

    this._setOnMessage(worker);

    return this as unknown as _AppWorker<
      W & {
        [K in N]: WT;
      }
    >;
  }

  _setOnMessage(worker: Worker) {
    worker.onmessage = (ev: {
      data: {
        callbackId: string;
        result: any;
        error: any;
      };
    }) => {
      const { callbackId, result, error } = ev.data;

      emitter.emit(callbackId, { result, error });
    };
  }

  _registerCallback(callbackId: string, callback: GeneralFunction) {
    emitter.once(callbackId, callback);
  }

  get<N extends keyof W>(workerName: N) {
    const worker = this._workerScripts[workerName];

    if (!worker) {
      const error = new Error(
        `"${workerName as string}" is not worker. Please load this worker`,
      );
      throw error;
    }

    return worker;
  }

  request<
    N extends keyof W,
    RN extends keyof W[N],
    P extends Parameters<W[N][RN]>[0],
    R extends Awaited<ReturnType<W[N][RN]>>,
  >(workerName: N, name: RN, payload?: P, transferList?: Transferable[]) {
    const worker = this._workerScripts[workerName] as Nullish<Worker>;
    const callbackId = uuid();

    if (!worker) {
      const error = new Error(
        `"${workerName as string}" is not worker. Please load this worker`,
      );
      return Promise.reject(error);
    }

    const returnValue = new Promise<R>((resolve, reject) => {
      this._registerCallback(
        callbackId,
        ({ result, error }: { result: R; error: Nullish<Error> }) => {
          error ? reject(error) : resolve(result);
        },
      );

      worker.postMessage(
        {
          name,
          payload,
          callbackId,
        },
        transferList as Transferable[],
      );
    });

    return returnValue;
  }

  onMessage<N extends keyof W>(
    workerName: N,
    handler: <T>(payload: T) => void,
  ) {
    const worker = this._workerScripts[workerName] as Nullish<Worker>;

    if (!worker) {
      const error = new Error(
        `"${workerName as string}" is not worker. Please load this worker`,
      );

      return Promise.reject(error);
    }

    worker.onmessage = (ev: { data: any }) => {
      handler(ev.data);
    };
  }

  terminateAll() {
    Object.values(this._workerScripts).forEach(worker => worker?.terminate());

    return this;
  }
}

export default _AppWorker;
